import React from 'react';
import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import {
    OrderViewCancellation,
    OrderViewCancellationActions,
    OrderViewCancellationBox,
    OrderViewCancellationCounter,
    OrderViewCancellationField,
    OrderViewCancellationFieldHeader,
    OrderViewCancellationText,
    OrderViewCancellationTextarea,
    OrderViewDangerButton,
    OrderViewLinkButton,
    OrderViewSection,
} from '../styles';
import AccentNotice from '../components/AccentNotice';
import Spinner from '../components/Spinner';
import { sdkErrorToNotice } from '../sdkErrorNotice';
import { useEcommL10n, useEcommStr } from '../useEcommStr';
import {
    CANCELLATION_REASON_MAX_LENGTH,
    useCancellationRequest,
} from '../useCancellationRequest';
import { readOrderIdFromUrl } from '../useOrder';

const REASON_FIELD_ID = 'order-cancellation-reason';

/** Backend-computed eligibility — the single source of truth for the button. */
export function canRequestCancellation(order: OrderResponseDto): boolean {
    return order.can_request_cancellation === true;
}

/** True once a cancellation request exists for this order (incl. on reload). */
export function isCancellationRequested(order: OrderResponseDto): boolean {
    return order.order_status === 'CANCELLATION_REQUESTED';
}

interface CancellationSectionProps {
    order: OrderResponseDto;
}

const CancellationSection: React.FC<CancellationSectionProps> = ({ order }) => {
    const str = useEcommStr();
    const l10n = useEcommL10n();
    const request = useCancellationRequest({
        orderId: readOrderIdFromUrl() ?? order.id,
    });

    const alreadyRequested = isCancellationRequested(order);
    const errorNotice = request.error
        ? sdkErrorToNotice(request.error, l10n, str)
        : null;

    return (
        <OrderViewCancellation data-auto='order-cancellation'>
            {errorNotice && (
                <AccentNotice
                    level='error'
                    title={errorNotice.title}
                    description={errorNotice.description}
                    dataAuto='order-cancellation-error'
                />
            )}
            <OrderViewSection>
                {request.succeeded || alreadyRequested ? (
                    <AccentNotice
                        level='success'
                        title={str(
                            'nativeEcommRuntime.order.cancellation.success_title'
                        )}
                        description={str(
                            'nativeEcommRuntime.order.cancellation.success_description'
                        )}
                        dataAuto='order-cancellation-success'
                    />
                ) : (
                    <OrderViewCancellationBox>
                        {!request.isConfirmVisible ? (
                            <OrderViewCancellationText>
                                {str(
                                    'nativeEcommRuntime.order.cancellation.description'
                                )}{' '}
                                <OrderViewLinkButton
                                    type='button'
                                    data-auto='order-cancellation-request'
                                    onClick={request.start}
                                >
                                    {str(
                                        'nativeEcommRuntime.order.cancellation.request'
                                    )}
                                </OrderViewLinkButton>
                            </OrderViewCancellationText>
                        ) : (
                            <>
                                <OrderViewCancellationField>
                                    <OrderViewCancellationFieldHeader>
                                        <label htmlFor={REASON_FIELD_ID}>
                                            {str(
                                                'nativeEcommRuntime.order.cancellation.reason_label'
                                            )}
                                        </label>
                                        <OrderViewCancellationCounter
                                            $isFull={
                                                request.reason.length ===
                                                CANCELLATION_REASON_MAX_LENGTH
                                            }
                                        >
                                            {request.reason.length} /{' '}
                                            {CANCELLATION_REASON_MAX_LENGTH}
                                        </OrderViewCancellationCounter>
                                    </OrderViewCancellationFieldHeader>
                                    <OrderViewCancellationTextarea
                                        id={REASON_FIELD_ID}
                                        rows={3}
                                        maxLength={
                                            CANCELLATION_REASON_MAX_LENGTH
                                        }
                                        value={request.reason}
                                        placeholder={str(
                                            'nativeEcommRuntime.order.cancellation.reason_placeholder'
                                        )}
                                        data-auto='order-cancellation-reason'
                                        onChange={(event) =>
                                            request.setReason(
                                                event.target.value
                                            )
                                        }
                                    />
                                </OrderViewCancellationField>

                                <OrderViewCancellationActions>
                                    <OrderViewDangerButton
                                        type='button'
                                        disabled={request.isRequesting}
                                        data-auto='order-cancellation-confirm'
                                        onClick={() => void request.confirm()}
                                    >
                                        {request.isRequesting && (
                                            <Spinner size={14} />
                                        )}
                                        {request.isRequesting
                                            ? str(
                                                  'nativeEcommRuntime.order.cancellation.confirming'
                                              )
                                            : str(
                                                  'nativeEcommRuntime.order.cancellation.confirm'
                                              )}
                                    </OrderViewDangerButton>
                                    {!request.isRequesting && (
                                        <OrderViewLinkButton
                                            type='button'
                                            data-auto='order-cancellation-keep'
                                            onClick={request.dismiss}
                                        >
                                            {str(
                                                'nativeEcommRuntime.order.cancellation.keep'
                                            )}
                                        </OrderViewLinkButton>
                                    )}
                                </OrderViewCancellationActions>
                            </>
                        )}
                    </OrderViewCancellationBox>
                )}
            </OrderViewSection>
        </OrderViewCancellation>
    );
};

export default CancellationSection;
